import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isFilledString from "~/helpers/isFilledString";

export const OVERHEAD_RATES_BASE_PATH = "/produce/capacity/overhead-rates";

export const OVERHEAD_RATES_DOCK_PANEL_ID = "overhead-rates";

export const OVERHEAD_RATE_HISTORY_TARGET_KIND = "overheadRateHistory";

export interface OverheadRateHistoryScope {
	effectiveOn: null | string;
	readOnly?: boolean;
	siteLocationId: string;
	subLocationId: null | string;
}

const optionalId = (value: unknown): null | string => (isFilledString(value) ? value : null);

export const buildOverheadRateHistoryTarget = ({
	effectiveOn,
	readOnly,
	siteLocationId,
	subLocationId
}: OverheadRateHistoryScope): ContinuationTarget => ({
	targetId: JSON.stringify({ effectiveOn, siteLocationId, subLocationId, ...(readOnly ? { readOnly: true } : {}) }),
	targetKind: OVERHEAD_RATE_HISTORY_TARGET_KIND
});

const parseTargetId = (targetId: string): Record<string, unknown> | null => {
	try {
		const parsed: unknown = JSON.parse(targetId);
		return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
	} catch {
		return null;
	}
};

export const readOverheadRateHistoryScope = (
	target: ContinuationTarget | undefined
): OverheadRateHistoryScope | null => {
	if (target?.targetKind !== OVERHEAD_RATE_HISTORY_TARGET_KIND) return null;
	const parsed = parseTargetId(target.targetId);
	const siteLocationId = optionalId(parsed?.siteLocationId);
	if (!siteLocationId) return null;
	return {
		...(parsed?.readOnly === true ? { readOnly: true } : {}),
		effectiveOn: optionalId(parsed?.effectiveOn),
		siteLocationId,
		subLocationId: optionalId(parsed?.subLocationId)
	};
};

const overheadRateHistoryReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => (readOverheadRateHistoryScope(target) ? OVERHEAD_RATES_BASE_PATH : ""),
	matchesRoute: () => null
};

export default overheadRateHistoryReceiverIdentityAdapter;
